import { useState, useEffect } from 'react';
import { Eye, Search, Download, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';

interface AuditLog {
  id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  created_at: string;
  user_email?: string;
  user_name?: string;
}

interface AuditLogsProps {
  className?: string;
}

export const AuditLogs: React.FC<AuditLogsProps> = ({ className }) => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, actionFilter, resourceFilter]);

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      
      // TODO: Replace with real audit logs once types are updated
      // For now using mock data as audit_logs table was just created
      const mockLogs: AuditLog[] = [
        {
          id: '1',
          user_id: 'user1',
          action: 'login',
          resource_type: 'authentication',
          resource_id: 'session_123',
          details: { success: true, method: 'password' },
          ip_address: '192.168.1.100',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          created_at: new Date().toISOString(),
          user_email: 'admin@shiftask.app',
          user_name: 'System Administrator'
        },
        {
          id: '2',
          user_id: 'user2',
          action: 'create',
          resource_type: 'guard',
          resource_id: 'guard_456',
          details: { name: 'John Doe', role: 'Security Guard' },
          ip_address: '192.168.1.101',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          created_at: new Date(Date.now() - 1800000).toISOString(),
          user_email: 'manager@shiftask.app',
          user_name: 'Team Manager'
        },
        {
          id: '3',
          user_id: 'user1',
          action: 'update',
          resource_type: 'team',
          resource_id: 'team_789',
          details: { field: 'name', old_value: 'Alpha Team', new_value: 'Alpha Security Team' },
          ip_address: '192.168.1.100',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          created_at: new Date(Date.now() - 3600000).toISOString(),
          user_email: 'admin@shiftask.app',
          user_name: 'System Administrator'
        },
        {
          id: '4',
          user_id: 'user3',
          action: 'delete',
          resource_type: 'schedule',
          resource_id: 'schedule_321',
          details: { reason: 'Cancelled shift' },
          ip_address: '192.168.1.102',
          user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)',
          created_at: new Date(Date.now() - 7200000).toISOString(),
          user_email: 'supervisor@shiftask.app',
          user_name: 'Field Supervisor'
        },
        {
          id: '5',
          user_id: 'user2',
          action: 'login',
          resource_type: 'authentication',
          resource_id: 'session_567',
          details: { success: true, method: 'password' },
          ip_address: '192.168.1.101',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          created_at: new Date(Date.now() - 10800000).toISOString(),
          user_email: 'manager@shiftask.app',
          user_name: 'Team Manager'
        }
      ];
      
      setLogs(mockLogs);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch audit logs. Please try again.",
        variant: "destructive"
      });
      
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.resource_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }

    if (resourceFilter !== 'all') {
      filtered = filtered.filter(log => log.resource_type === resourceFilter);
    }

    setFilteredLogs(filtered);
  };

  const exportLogs = async () => {
    try {
      const csv = convertToCSV(filteredLogs);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `audit_logs_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      link.click();

      toast({
        title: "Export Successful",
        description: "Audit logs have been exported to CSV."
      });
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export audit logs.",
        variant: "destructive"
      });
    }
  };

  const convertToCSV = (data: AuditLog[]) => {
    const headers = ['Date', 'User', 'Action', 'Resource Type', 'Resource ID', 'IP Address'];
    const rows = data.map(log => [
      format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
      log.user_email || 'Unknown',
      log.action,
      log.resource_type,
      log.resource_id,
      log.ip_address
    ]);

    return [headers, ...rows].map(row => 
      row.map(field => `"${field}"`).join(',')
    ).join('\n');
  };

  const getActionBadgeVariant = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
        return 'default';
      case 'update':
        return 'secondary';
      case 'delete':
        return 'destructive';
      case 'login':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const uniqueActions = [...new Set(logs.map(log => log.action))];
  const uniqueResourceTypes = [...new Set(logs.map(log => log.resource_type))];

  return (
    <div className={className}>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-primary" />
            <div>
              <h3 className="text-lg font-semibold">Audit Logs</h3>
              <p className="text-sm text-muted-foreground">
                Track all user actions and system events
              </p>
            </div>
          </div>
          <Button onClick={exportLogs} variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {uniqueActions.map(action => (
                <SelectItem key={action} value={action}>{action}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={resourceFilter} onValueChange={setResourceFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by resource" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Resources</SelectItem>
              {uniqueResourceTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date & Time</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="w-20">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mx-auto"></div>
                    <p className="text-sm text-muted-foreground mt-2">Loading audit logs...</p>
                  </TableCell>
                </TableRow>
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <p className="text-muted-foreground">No audit logs found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {format(new Date(log.created_at), 'MMM dd, yyyy')}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(log.created_at), 'HH:mm:ss')}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{log.user_name}</div>
                        <div className="text-sm text-muted-foreground">{log.user_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getActionBadgeVariant(log.action)}>
                        {log.action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{log.resource_type}</div>
                        <div className="text-sm text-muted-foreground font-mono">
                          {log.resource_id}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{log.ip_address}</span>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedLog(log)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>Audit Log Details</DialogTitle>
                          </DialogHeader>
                          {selectedLog && (
                            <ScrollArea className="max-h-96">
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="font-semibold">User</Label>
                                    <p>{selectedLog.user_name} ({selectedLog.user_email})</p>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Action</Label>
                                    <Badge variant={getActionBadgeVariant(selectedLog.action)}>
                                      {selectedLog.action}
                                    </Badge>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Resource Type</Label>
                                    <p>{selectedLog.resource_type}</p>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Resource ID</Label>
                                    <p className="font-mono text-sm">{selectedLog.resource_id}</p>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">IP Address</Label>
                                    <p className="font-mono">{selectedLog.ip_address}</p>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Date & Time</Label>
                                    <p>{format(new Date(selectedLog.created_at), 'MMM dd, yyyy HH:mm:ss')}</p>
                                  </div>
                                </div>
                                
                                <Separator />
                                
                                <div>
                                  <Label className="font-semibold">User Agent</Label>
                                  <p className="text-sm font-mono bg-muted p-2 rounded mt-1">
                                    {selectedLog.user_agent}
                                  </p>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold">Additional Details</Label>
                                  <pre className="text-sm bg-muted p-3 rounded mt-1 overflow-auto">
                                    {JSON.stringify(selectedLog.details, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            </ScrollArea>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          Showing {filteredLogs.length} of {logs.length} logs
        </div>
      </Card>
    </div>
  );
};